/**
 * ساعتِ تستر، دو روش کنارِ هم — فقط برای ادمین (تبِ «تستِ دستی»).
 *
 * «قدیم» عددِ اصلی است و همان قاعده‌ی clockByRun ِ qa/index.html: ساعت با
 * start/resume راه می‌افتد، با pause می‌ایستد (stoppedAt مقدم)، هر تکه مالِ
 * اجرای (تیکت/سناریو#تلاش) ای است که آن را شروع کرد، و هر رویدادِ همان
 * سناریو تکه را جلو می‌برد. این فایل آن عدد را عوض نمی‌کند.
 *
 * «جدید» فقط برای دوباره چک کردن است؛ از «قدیم» کم یا به آن اضافه می‌کند:
 *   − خواب: فاصله‌ی بیش از SLEEP_CAP بینِ دو رویدادِ پشتِ سرِ هم (حتی beat ِ
 *     هر ۳۰ ثانیه نرسیده) یعنی دستگاه خواب بوده؛ فقط SLEEP_CAP حساب می‌شود.
 *   − بی‌کاری: بعد از IDLE بدونِ هیچ رویدادِ واقعی (هر چیزی جز beat) باقی‌اش نه.
 *   − جای دیگر: وقتی صفحه پنهان بوده و در ±AWAY_WINDOW هیچ فعالیتی از حساب‌های
 *     تستر در اپ یا سایت (qa_app_activity، develop و production) ثبت نشده.
 *   + بات: هر سؤال در @Outami_testers_bot از BOT_BEFORE پیش از سؤال تا
 *     BOT_AFTER بعد از جواب، منهای هر بخشی که ساعت (قدیم) روشن بوده.
 */

export const SLEEP_CAP = 5 * 60_000;
export const IDLE = 10 * 60_000;
export const AWAY_WINDOW = 3 * 60_000;
export const BOT_BEFORE = 60_000;
export const BOT_AFTER = 60_000;
const SLICE = 15_000;

export interface ClockEvent {
  type: string; t: number; sid?: string | null; k?: string | null; a?: number | null; p?: number | null;
  d?: { stoppedAt?: number };
}
export interface BotTurn { asked: number; answered: number; k: string | null; sid: string | null }

export interface ClockRow {
  /** «KEY/ID#a» — همان کلیدِ صفحه‌ی QA. */
  key: string; k: string | null; sid: string | null; a: number;
  old: number; sleep: number; idle: number; away: number; bot: number;
  /** old − sleep − idle − away + bot */
  neu: number;
  /** زمانِ هر صفحه (p: 0 = نمای کلی، 1..n قدم‌ها) با هر دو روش؛ بات جزوِ هیچ قدمی نیست. */
  pages: Record<number, { old: number; neu: number }>;
  first: number;
}

const evK = (e: ClockEvent) => e.k || null;
const rowKey = (k: string | null, sid: string | null, a: number) => `${k ? `${k}/` : ''}${sid ?? ''}#${a}`;

/**
 * اجراهایی که تستر «کنار گذاشته» (رویدادِ discard، ۲۰۲۶/۱۰/۲۷): انگار هیچ‌وقت
 * شروع نشده‌اند، پس ساعتشان صفر است. همان قاعده‌ی discardedRuns ِ qa/index.html.
 */
export const discardKey = (k: string | null | undefined, sid: string | null | undefined, a: number | null | undefined) =>
  `${k || ''}|${sid ?? ''}|${a || 1}`;
export const discardedRuns = (events: { type: string; k?: string | null; sid?: string | null; a?: number | null }[]) =>
  new Set(events.filter((e) => e.type === 'discard').map((e) => discardKey(e.k, e.sid, e.a)));

/** آیا در ±AWAY_WINDOW از t فعالیتی هست؟ act مرتب است. */
function near(act: number[], t: number): boolean {
  let lo = 0, hi = act.length;
  while (lo < hi) { const m = (lo + hi) >> 1; if (act[m] < t - AWAY_WINDOW) lo = m + 1; else hi = m; }
  return lo < act.length && act[lo] <= t + AWAY_WINDOW;
}

/**
 * ردیف‌های یک تستر. events همه‌ی رویدادهای همان تستر؛ activity زمانِ (ms)
 * فعالیت‌هایش در اپ/سایت؛ turns سؤال‌هایش در بات.
 */
export function clockRows(events: ClockEvent[], activity: number[] = [], turns: BotTurn[] = []): ClockRow[] {
  const act = [...activity].sort((x, y) => x - y);
  const out = new Map<string, ClockRow>();
  const running: [number, number][] = [];
  const get = (k: string | null, sid: string | null, a: number, t: number) => {
    const key = rowKey(k, sid, a);
    let x = out.get(key);
    if (!x) { x = { key, k, sid, a, old: 0, sleep: 0, idle: 0, away: 0, bot: 0, neu: 0, pages: {}, first: t }; out.set(key, x); }
    return x;
  };
  type Run = { t: number; k: string | null; sid: string | null; a: number; p: number; hidden: boolean; act: number };
  const add = (r: Run, to: number) => {
    if (to <= r.t) return;
    const x = get(r.k, r.sid, r.a, r.t);
    const old = to - r.t;
    const capEnd = Math.min(to, r.t + SLEEP_CAP);
    const sleep = to - capEnd;
    const end = Math.max(r.t, Math.min(capEnd, r.act + IDLE));
    const idle = capEnd - end;
    let away = 0;
    if (r.hidden) for (let s = r.t; s < end; s += SLICE) { const d = Math.min(SLICE, end - s); if (!near(act, s + d / 2)) away += d; }
    const neu = old - sleep - idle - away;
    x.old += old; x.sleep += sleep; x.idle += idle; x.away += away; x.neu += neu;
    const pg = (x.pages[r.p] ||= { old: 0, neu: 0 });
    pg.old += old; pg.neu += neu;
  };
  let run: Run | null = null;
  let runStart = 0;
  for (const e of [...events].sort((x, y) => x.t - y.t)) {
    if ((e.type === 'start' || e.type === 'resume') && run == null) {
      run = { t: e.t, k: evK(e), sid: e.sid ?? null, a: e.a || 1, p: e.p ?? 0, hidden: false, act: e.t };
      runStart = e.t;
    } else if (e.type === 'pause' && run != null) {
      const end = Math.min(e.d?.stoppedAt || e.t, e.t);
      add(run, end); running.push([runStart, end]); run = null;
    } else if (run != null && e.sid === run.sid && evK(e) === run.k) {
      const r: Run = run;
      add(r, e.t);
      run = {
        ...r, t: e.t, a: e.a || r.a, p: e.p == null ? r.p : e.p,
        hidden: e.type === 'hidden' ? true : e.type === 'visible' ? false : r.hidden,
        act: e.type === 'beat' ? r.act : e.t,
      };
    }
  }

  // بات: سؤال‌های پشتِ سرِ هم یکی می‌شوند؛ هر بخش که ساعت روشن بوده کم می‌شود.
  const covered = (from: number, to: number) =>
    running.reduce((s, [x, y]) => s + Math.max(0, Math.min(to, y) - Math.max(from, x)), 0);
  const groups = new Map<string, { k: string | null; sid: string | null; iv: [number, number][] }>();
  for (const b of turns) {
    const g = `${b.k ?? ''}|${b.sid ?? ''}`;
    const x = groups.get(g) ?? { k: b.k, sid: b.sid, iv: [] };
    x.iv.push([b.asked - BOT_BEFORE, b.answered + BOT_AFTER]);
    groups.set(g, x);
  }
  for (const g of groups.values()) {
    const iv = g.iv.sort((x, y) => x[0] - y[0]);
    const merged: [number, number][] = [];
    for (const [s, e] of iv) { const l = merged[merged.length - 1]; if (l && s <= l[1]) l[1] = Math.max(l[1], e); else merged.push([s, e]); }
    const ms = merged.reduce((s, [a, b]) => s + (b - a) - covered(a, b), 0);
    if (ms <= 0) continue;
    // به آخرین تلاشِ همان سناریو، وگرنه یک ردیفِ تازه با old = 0.
    const latest = [...out.values()].filter((x) => x.k === g.k && x.sid === g.sid).sort((x, y) => y.a - x.a)[0];
    const x = latest ?? get(g.k, g.sid, 1, merged[0][0]);
    x.bot += ms; x.neu += ms;
  }
  const gone = discardedRuns(events);
  return [...out.values()].filter((x) => !gone.has(discardKey(x.k, x.sid, x.a))).sort((x, y) => x.first - y.first);
}

export interface StepAvg { p: number; old: number; neu: number }
export interface ScenarioAvg { k: string | null; sid: string; runs: number; old: number; neu: number; steps: StepAvg[] }

/**
 * میانگینِ هر سناریو (روی اجراهایش، هر تلاش یک اجرا) و هر قدمِ آن. ردیف‌های
 * کمتر از یک دقیقه (باز و بسته کردن) شمرده نمی‌شوند. byTicket=false سناریو را
 * از همه‌ی تیکت‌ها با هم می‌گیرد.
 */
export function scenarioAverages(rows: ClockRow[], byTicket = false): ScenarioAvg[] {
  const g = new Map<string, ClockRow[]>();
  for (const r of rows) {
    if (!r.sid || r.old + r.bot < 60_000) continue;
    const key = `${byTicket ? r.k ?? '' : ''}|${r.sid}`;
    g.set(key, [...(g.get(key) ?? []), r]);
  }
  return [...g.values()].map((rs) => {
    const n = rs.length;
    const ps = [...new Set(rs.flatMap((r) => Object.keys(r.pages).map(Number)))].sort((x, y) => x - y);
    return {
      k: byTicket ? rs[0].k : null, sid: rs[0].sid!, runs: n,
      old: rs.reduce((s, r) => s + r.old, 0) / n,
      neu: rs.reduce((s, r) => s + r.neu, 0) / n,
      steps: ps.map((p) => ({
        p,
        old: rs.reduce((s, r) => s + (r.pages[p]?.old ?? 0), 0) / n,
        neu: rs.reduce((s, r) => s + (r.pages[p]?.neu ?? 0), 0) / n,
      })),
    };
  }).sort((x, y) => x.sid.localeCompare(y.sid));
}

export const sumRows = (rows: ClockRow[]) => rows.reduce(
  (s, r) => ({ old: s.old + r.old, sleep: s.sleep + r.sleep, idle: s.idle + r.idle, away: s.away + r.away, bot: s.bot + r.bot, neu: s.neu + r.neu }),
  { old: 0, sleep: 0, idle: 0, away: 0, bot: 0, neu: 0 },
);
